import React, { useState } from 'react';
import Card from '../../components/ui/Card';
import { CalendarIcon, DownloadIcon, ChevronLeftIcon, ChevronRightIcon, AlertCircleIcon, PlayCircleIcon } from 'lucide-react';
const mockAggregators = [{
  id: 'agg1',
  name: 'Global Payments Network'
}, {
  id: 'agg2',
  name: 'Metro Financial Services'
}, {
  id: 'agg3',
  name: 'Digital Commerce Hub'
}, {
  id: 'agg4',
  name: 'PayTech Solutions'
}];
const mockMerchants = [{
  id: 'mer1',
  name: 'Global Electronics',
  aggregatorId: 'agg1'
}, {
  id: 'mer2',
  name: 'Fashion Boutique',
  aggregatorId: 'agg1'
}, {
  id: 'mer3',
  name: 'Tech Store',
  aggregatorId: 'agg2'
}, {
  id: 'mer4',
  name: 'Food Court',
  aggregatorId: 'agg2'
}, {
  id: 'mer5',
  name: 'Book Shop',
  aggregatorId: 'agg3'
}, {
  id: 'mer6',
  name: 'Sports Gear',
  aggregatorId: 'agg3'
}];
const generateFailedPayments = () => {
  const failed = [];
  mockMerchants.forEach(merchant => {
    const failedCount = Math.floor(Math.random() * 15) + 1;
    const failedAmount = (Math.random() * 5000 + 500).toFixed(2);
    const reasons = ['Insufficient Funds', 'Network Error', 'Invalid Card', 'Timeout', 'Declined'];
    const mainReason = reasons[Math.floor(Math.random() * reasons.length)];
    failed.push({
      merchantId: merchant.id,
      merchantName: merchant.name,
      aggregatorId: merchant.aggregatorId,
      failedCount,
      failedAmount: parseFloat(failedAmount),
      mainFailureReason: mainReason,
      failureRate: (failedCount / (failedCount + Math.random() * 100) * 100).toFixed(2)
    });
  });
  return failed;
};
const FailedPaymentReports: React.FC = () => {
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    date.setDate(1);
    return date.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [selectedAggregator, setSelectedAggregator] = useState('');
  const [selectedMerchant, setSelectedMerchant] = useState('');
  const [failedPayments, setFailedPayments] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [reportGenerated, setReportGenerated] = useState(false);
  const itemsPerPage = 10;
  const filteredMerchants = selectedAggregator ? mockMerchants.filter(m => m.aggregatorId === selectedAggregator) : mockMerchants;
  const generateReport = () => {
    let failedFiltered = generateFailedPayments();
    if (selectedAggregator) {
      failedFiltered = failedFiltered.filter(f => f.aggregatorId === selectedAggregator);
    }
    if (selectedMerchant) {
      failedFiltered = failedFiltered.filter(f => f.merchantId === selectedMerchant);
    }
    setFailedPayments(failedFiltered);
    setCurrentPage(1);
    setReportGenerated(true);
  };
  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return failedPayments.slice(startIndex, endIndex);
  };
  const getTotalPages = () => {
    return Math.ceil(failedPayments.length / itemsPerPage);
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 2
    }).format(amount);
  };
  const resetFilters = () => {
    const date = new Date();
    date.setDate(1);
    setFromDate(date.toISOString().split('T')[0]);
    setToDate(new Date().toISOString().split('T')[0]);
    setSelectedAggregator('');
    setSelectedMerchant('');
    setFailedPayments([]);
    setReportGenerated(false);
  };
  return <div className="space-y-6" data-id="element-5088">
      <div data-id="element-5089">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-5090">
          Failed Payment Reports
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-5091">
          View failed payment information grouped by merchants
        </p>
      </div>
      <Card data-id="element-5092">
        <div className="flex items-center justify-between mb-4" data-id="element-5093">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-5094">
            Filters
          </h3>
          <button onClick={resetFilters} className="text-sm text-blue-600 dark:text-blue-400 hover:underline" data-id="element-5095">
            Reset Filters
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-5096">
          <div data-id="element-5097">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5098">
              From Date
            </label>
            <div className="relative" data-id="element-5099">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-5100">
                <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-5101" />
              </div>
              <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800" data-id="element-5102" />
            </div>
          </div>
          <div data-id="element-5103">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5104">
              To Date
            </label>
            <div className="relative" data-id="element-5105">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-5106">
                <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-5107" />
              </div>
              <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800" data-id="element-5108" />
            </div>
          </div>
          <div data-id="element-5109">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5110">
              Aggregator
            </label>
            <select value={selectedAggregator} onChange={e => {
            setSelectedAggregator(e.target.value);
            setSelectedMerchant('');
          }} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800" data-id="element-5111">
              <option value="" data-id="element-5112">All Aggregators</option>
              {mockAggregators.map(agg => <option key={agg.id} value={agg.id} data-id="element-5113">
                  {agg.name}
                </option>)}
            </select>
          </div>
          <div data-id="element-5114">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5115">
              Merchant
            </label>
            <select value={selectedMerchant} onChange={e => setSelectedMerchant(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800" data-id="element-5116">
              <option value="" data-id="element-5117">All Merchants</option>
              {filteredMerchants.map(merchant => <option key={merchant.id} value={merchant.id} data-id="element-5118">
                  {merchant.name}
                </option>)}
            </select>
          </div>
        </div>
        <div className="mt-6 flex justify-end" data-id="element-5119">
          <button onClick={generateReport} className="inline-flex items-center px-6 py-2.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-5120">
            <PlayCircleIcon className="h-5 w-5 mr-2" data-id="element-5121" />
            Generate Report
          </button>
        </div>
      </Card>
      <Card data-id="element-5122">
        <div className="flex items-center justify-between mb-4" data-id="element-5123">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-5124">
            Failed Payments
          </h3>
          {reportGenerated && failedPayments.length > 0 && <button onClick={() => console.log('Export')} className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-5125">
              <DownloadIcon className="h-4 w-4 mr-2" data-id="element-5126" />
              Export
            </button>}
        </div>
        {!reportGenerated ? <div className="text-center py-12" data-id="element-5127">
            <AlertCircleIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-5128" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white" data-id="element-5129">
              No Report Generated
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-5130">
              Select your filters above and click "Generate Report" to view
              failed payments.
            </p>
          </div> : failedPayments.length === 0 ? <div className="text-center py-12" data-id="element-5131">
            <AlertCircleIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-5132" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white" data-id="element-5133">
              No Failed Payments Found
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-5134">
              No failed payments match your selected filters. Try adjusting your
              criteria.
            </p>
          </div> : <>
            <div className="overflow-x-auto" data-id="element-5135">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-5136">
                <thead className="bg-gray-50 dark:bg-gray-700" data-id="element-5137">
                  <tr data-id="element-5138">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase" data-id="element-5139">
                      Merchant
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase" data-id="element-5140">
                      Failed Count
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase" data-id="element-5141">
                      Failed Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase" data-id="element-5142">
                      Failure Rate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase" data-id="element-5143">
                      Main Reason
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-5144">
                  {getCurrentPageData().map((failed: any) => <tr key={failed.merchantId} data-id="element-5145">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" data-id="element-5146">
                        {failed.merchantName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-5147">
                        {failed.failedCount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 dark:text-red-400" data-id="element-5148">
                        {formatCurrency(failed.failedAmount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-5149">
                        {failed.failureRate}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-5150">
                        {failed.mainFailureReason}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
            {getTotalPages() > 1 && <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 px-4 py-3 sm:px-6 mt-4" data-id="element-5151">
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between" data-id="element-5152">
                  <div data-id="element-5153">
                    <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-5154">
                      Showing{' '}
                      <span className="font-medium" data-id="element-5155">
                        {(currentPage - 1) * itemsPerPage + 1}
                      </span>{' '}
                      to{' '}
                      <span className="font-medium" data-id="element-5156">
                        {Math.min(currentPage * itemsPerPage, failedPayments.length)}
                      </span>{' '}
                      of{' '}
                      <span className="font-medium" data-id="element-5157">
                        {failedPayments.length}
                      </span>{' '}
                      results
                    </p>
                  </div>
                  <div data-id="element-5158">
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" data-id="element-5159">
                      <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50" data-id="element-5160">
                        <ChevronLeftIcon className="h-5 w-5" data-id="element-5161" />
                      </button>
                      {Array.from({
                  length: getTotalPages()
                }, (_, i) => i + 1).map(page => <button key={page} onClick={() => setCurrentPage(page)} className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === page ? 'z-10 bg-blue-50 dark:bg-blue-900/30 border-blue-500 text-blue-600 dark:text-blue-400' : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`} data-id="element-5162">
                          {page}
                        </button>)}
                      <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, getTotalPages()))} disabled={currentPage === getTotalPages()} className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50" data-id="element-5163">
                        <ChevronRightIcon className="h-5 w-5" data-id="element-5164" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>}
          </>}
      </Card>
    </div>;
};
export default FailedPaymentReports;